import { ElementType } from 'react';
import { InfinitePaginatedList } from '@frontend/components';
import { useDigitalFormsFreemiumContext } from '@frontend/digital-forms-scope';
import { useTranslation } from '@frontend/i18n';
import { PolymorphicComponentPropWithoutRef } from '@frontend/design-system';
import { useFormPacketList } from '../../hooks';
import { Category, FormOrPacketV2 } from '../../types/form-packet-selector.types';
import { FormPacketListItem } from './form-packet-list-item';

type FormPacketListProps = {
  formsListInfiniteQueryProps: ReturnType<typeof useFormPacketList>['formsQuery'];
  packetsListInfiniteQueryProps: ReturnType<typeof useFormPacketList>['packetsQuery'];
  onSelect: (item: FormOrPacketV2) => void;
  selectedId?: string;
  category: Category;
};

type PolymorphicProps<C extends ElementType> = PolymorphicComponentPropWithoutRef<C, FormPacketListProps>;

export const FormPacketList = <C extends ElementType = 'ul'>({
  formsListInfiniteQueryProps,
  packetsListInfiniteQueryProps,
  onSelect,
  selectedId,
  category,
}: PolymorphicProps<C>) => {
  const { t } = useTranslation('form-packet-selector');
  const { isFreemium } = useDigitalFormsFreemiumContext();

  const isFormsList = category === Category.FORMS;
  const infiniteListQueryProps = isFormsList ? formsListInfiniteQueryProps : packetsListInfiniteQueryProps;

  return (
    <InfinitePaginatedList
      infiniteQueryProps={infiniteListQueryProps}
      height='100%'
      renderListItem={({ listItem }) => (
        <FormPacketListItem
          key={listItem.id}
          isSelected={selectedId === listItem.id}
          name={listItem.name ?? ''}
          onSelect={() => onSelect(listItem)}
          iconName={category === Category.FORMS ? 'forms' : 'packet'}
          isLocked={category === Category.FORMS && isFreemium ? listItem?.isLocked : false}
        />
      )}
      emptyStateConfig={{
        description: t('No items to display.'),
      }}
    />
  );
};
